import React, { useMemo } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import Dropdown from 'core-system/Dropdown/Dropdown'
import TextField from 'core-system/TextField'
import { RuleField, RuleOperator } from './ruleTypes'
import {
  RuleConditionValue,
  asStringArray,
  emptyConditionValue,
  isListOperator,
} from './valueUtils'
import { FIELD_OPTIONS, OPERATOR_OPTIONS } from './ruleBuilderConstants'
import ListValueInput from './ListValueInput'

const Row = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;
`

const Cell = styled.div<{ $minWidth?: number }>`
  min-width: ${(p) => (p.$minWidth ? `${p.$minWidth}px` : '140px')};
  flex: 1 1 120px;
`

const CellLabel = styled(Text)`
  margin-bottom: 0.35rem;
  display: block;
  letter-spacing: 0.02em;
`

const ControlSlot = styled.div`
  min-height: 3.5rem;
  display: flex;
  align-items: center;
  width: 100%;
`

const ValueWrap = styled.div`
  width: 100%;
  input {
    border-radius: 0.5rem;
    border-color: ${(props) => props.theme.palette.grey.grey300};
    &:hover {
      border-color: ${(props) => props.theme.palette.grey.grey600};
    }
    &:focus {
      border-color: ${(props) => props.theme.palette.text.primary};
    }
  }
`

export type ConditionRowDensity = 'comfortable' | 'compact'

export interface ConditionRowProps {
  density?: ConditionRowDensity
  field: RuleField
  operator: RuleOperator
  value: RuleConditionValue
  onFieldChange: (field: RuleField) => void
  onOperatorChange: (op: RuleOperator) => void
  onValueChange: (value: RuleConditionValue) => void
  valuePlaceholder?: string
  /** Attribute options disabled (greyed) in the field dropdown. */
  excludeFields?: RuleField[]
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  density = 'comfortable',
  field,
  operator,
  value,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  valuePlaceholder = 'Value',
  excludeFields = [],
}) => {
  const inputHeight = density === 'compact' ? '44px' : '52px'
  const labelVariant = density === 'compact' ? ('caption' as const) : ('body2' as const)
  const listMode = isListOperator(operator)
  const scalarValue = typeof value === 'string' ? value : ''

  const fieldItems = useMemo(
    () =>
      FIELD_OPTIONS.map((opt) => ({
        ...opt,
        disabled: excludeFields.includes(opt.id),
      })),
    [excludeFields]
  )

  const handleOperatorChange = (next: RuleOperator) => {
    if (isListOperator(next) !== listMode) {
      onValueChange(emptyConditionValue(next))
    }
    onOperatorChange(next)
  }

  return (
    <Row flexDirection='row'>
      <Cell $minWidth={density === 'compact' ? 130 : 150}>
        <CellLabel variant={labelVariant} color='grey600' fontWeight={600}>
          Attribute
        </CellLabel>
        <ControlSlot>
          <Dropdown
            variant='contained'
            items={fieldItems}
            active={field}
            itemCallback={(item) => onFieldChange(item.id as RuleField)}
            placeholder='Select'
            usePortal={true}
          />
        </ControlSlot>
      </Cell>
      <Cell $minWidth={density === 'compact' ? 110 : 128}>
        <CellLabel variant={labelVariant} color='grey600' fontWeight={600}>
          Comparison
        </CellLabel>
        <ControlSlot>
          <Dropdown
            variant='contained'
            items={OPERATOR_OPTIONS}
            active={operator}
            itemCallback={(item) =>
              handleOperatorChange(item.id as RuleOperator)
            }
            placeholder='Select'
            usePortal={true}
          />
        </ControlSlot>
      </Cell>
      <Cell $minWidth={density === 'compact' ? 140 : 180}>
        <CellLabel variant={labelVariant} color='grey600' fontWeight={600}>
          Value
        </CellLabel>
        {listMode ? (
          <ListValueInput
            values={asStringArray(value)}
            onChange={onValueChange}
            placeholder='e.g. HQ, Remote'
            height={inputHeight}
          />
        ) : (
          <ControlSlot>
            <ValueWrap>
              <TextField
                value={scalarValue}
                onChange={(e) => onValueChange(e.target.value)}
                placeholder={valuePlaceholder}
                height={inputHeight}
              />
            </ValueWrap>
          </ControlSlot>
        )}
      </Cell>
    </Row>
  )
}

export default React.memo(ConditionRow)
